import React, { useEffect, useRef, useState, useCallback } from "react";
import { RouteCheckIn } from "../../Types";
import dayjs from "dayjs";
import { Button, Spin, Tooltip as AntTooltip } from "antd";
import { Navigation, MapPin, RefreshCw, Layers } from "lucide-react";

declare global {
  interface Window {
    L: any;
  }
}

interface InteractiveMapProps {
  locations: RouteCheckIn[];
  selectedLocationId?: string | null;
  onSelectLocation?: (location: RouteCheckIn) => void;
  loading?: boolean;
  height?: string;
}

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  locations = [],
  selectedLocationId,
  onSelectLocation,
  loading = false,
  height = "520px",
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const polylineLayerRef = useRef<any>(null);

  const [isLeafletReady, setIsLeafletReady] = useState<boolean>(
    typeof window !== "undefined" && !!window.L,
  );
  const [mapTileType, setMapTileType] = useState<"streets" | "satellite">(
    "streets",
  );

  // Load Leaflet CSS and JS dynamically if not loaded
  useEffect(() => {
    if (window.L) {
      setIsLeafletReady(true);
      return;
    }

    const cssId = "leaflet-css-cdn";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    const jsId = "leaflet-js-cdn";
    if (!document.getElementById(jsId)) {
      const script = document.createElement("script");
      script.id = jsId;
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.onload = () => setIsLeafletReady(true);
      document.body.appendChild(script);
    } else {
      const script = document.getElementById(jsId) as HTMLScriptElement;
      if (script) {
        script.addEventListener("load", () => setIsLeafletReady(true));
      }
    }
  }, []);

  // Initialize Leaflet Map Instance
  useEffect(() => {
    if (!isLeafletReady || !mapContainerRef.current || mapInstanceRef.current)
      return;

    const L = window.L;

    const initialLat =
      locations.length > 0
        ? parseFloat(String(locations[0].latitude))
        : 6.9271;
    const initialLng =
      locations.length > 0
        ? parseFloat(String(locations[0].longitude))
        : 79.8612;

    const map = L.map(mapContainerRef.current, {
      center: [initialLat, initialLng],
      zoom: 13,
      zoomControl: false,
    });

    L.control.zoom({ position: "topright" }).addTo(map);

    const streetTiles = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    );
    streetTiles.addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    polylineLayerRef.current = L.layerGroup().addTo(map);

    mapInstanceRef.current = map;

    setTimeout(() => {
      map.invalidateSize();
    }, 250);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isLeafletReady]);

  // Handle Tile Switcher (Streets vs Satellite)
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    const L = window.L;
    const map = mapInstanceRef.current;

    map.eachLayer((layer: any) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });

    if (mapTileType === "satellite") {
      L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        {
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and GIS User Community",
          maxZoom: 18,
        },
      ).addTo(map);
    } else {
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);
    }
  }, [mapTileType]);

  // Render Markers and Polyline Path
  useEffect(() => {
    if (!mapInstanceRef.current || !isLeafletReady) return;

    const L = window.L;
    const map = mapInstanceRef.current;
    const markersGroup = markersLayerRef.current;
    const polylineGroup = polylineLayerRef.current;

    if (!markersGroup || !polylineGroup) return;

    markersGroup.clearLayers();
    polylineGroup.clearLayers();

    if (!locations || locations.length === 0) return;

    const validLocations = [...locations]
      .filter(
        (loc) =>
          loc.latitude != null &&
          loc.longitude != null &&
          !isNaN(Number(loc.latitude)),
      )
      .sort(
        (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      );

    if (validLocations.length === 0) return;

    const latLngs: [number, number][] = [];

    validLocations.forEach((loc, index) => {
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      latLngs.push([lat, lng]);

      const isSelected = selectedLocationId === loc.id;
      const pointNum = index + 1;

      const markerHtml = `
        <div class="relative flex items-center justify-center group cursor-pointer">
          <div class="absolute w-8 h-8 rounded-full ${
            isSelected
              ? "bg-red-500 animate-ping opacity-75"
              : "bg-red-400 opacity-40"
          }"></div>
          <div class="relative w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center font-bold text-white text-xs ${
            isSelected
              ? "bg-red-700 scale-125 z-30 ring-4 ring-red-300"
              : "bg-red-600 hover:scale-110"
          } transition-all duration-200">
            ${pointNum}
          </div>
        </div>
      `;

      const customIcon = L.divIcon({
        html: markerHtml,
        className: "custom-leaflet-marker",
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -18],
      });

      const marker = L.marker([lat, lng], { icon: customIcon });

      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      const timeFormatted = dayjs(loc.created_at).format("YYYY-MM-DD hh:mm A");

      const popupContent = `
        <div class="p-3 min-w-[220px] max-w-[280px] font-sans">
          <div class="flex items-center justify-between gap-2 border-b pb-2 mb-2">
            <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white font-bold text-xs">
              ${pointNum}
            </span>
            <span class="font-bold text-gray-800 text-sm flex-1 truncate">${
              loc.user_name || loc.employee_name || "User"
            }</span>
            <span class="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">${
              loc.action || "Check-In"
            }</span>
          </div>
          
          <div class="space-y-1.5 text-xs text-gray-600">
            ${
              loc.route_name
                ? `<div class="flex items-center justify-between"><span class="text-gray-400">Route:</span> <span class="font-medium text-gray-700">${loc.route_name}</span></div>`
                : ""
            }
            ${
              loc.order_number
                ? `<div class="flex items-center justify-between"><span class="text-gray-400">Order #:</span> <span class="font-bold text-blue-600">${loc.order_number}</span></div>`
                : ""
            }
            <div class="flex items-center justify-between"><span class="text-gray-400">Time:</span> <span class="font-medium text-gray-700">${timeFormatted}</span></div>
            <div class="flex items-center justify-between"><span class="text-gray-400">Coords:</span> <span class="font-mono text-[11px] text-gray-600">${lat.toFixed(
              5,
            )}, ${lng.toFixed(5)}</span></div>
            ${
              loc.notes
                ? `<div class="bg-gray-50 p-1.5 rounded text-gray-600 italic text-[11px] mt-1 border border-gray-100">${loc.notes}</div>`
                : ""
            }
          </div>

          <div class="mt-3 pt-2 border-t flex items-center justify-between">
            <a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
              <span>Directions</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
            </a>
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, { maxWidth: 300 });

      marker.on("click", () => {
        if (onSelectLocation) {
          onSelectLocation(loc);
        }
      });

      markersGroup.addLayer(marker);

      if (isSelected) {
        map.flyTo([lat, lng], 15, { duration: 0.8 });
        marker.openPopup();
      }
    });

    if (latLngs.length > 1) {
      const polyline = L.polyline(latLngs, {
        color: "#dc2626",
        weight: 3,
        opacity: 0.8,
        dashArray: "6, 8",
        lineCap: "round",
      });
      polylineGroup.addLayer(polyline);
    }

    if (latLngs.length > 0 && !selectedLocationId) {
      const bounds = L.latLngBounds(latLngs);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [locations, selectedLocationId, isLeafletReady, onSelectLocation]);

  // Recenter Map Handler
  const handleRecenter = useCallback(() => {
    if (!mapInstanceRef.current || locations.length === 0 || !window.L) return;
    const L = window.L;
    const validLocations = locations.filter(
      (loc) => loc.latitude != null && loc.longitude != null,
    );
    if (validLocations.length === 0) return;

    const bounds = L.latLngBounds(
      validLocations.map((loc) => [
        Number(loc.latitude),
        Number(loc.longitude),
      ]),
    );
    mapInstanceRef.current.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 16,
    });
  }, [locations]);

  return (
    <div
      className="relative w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-100"
      style={{ height }}
    >
      {/* Loading Overlay */}
      {(!isLeafletReady || loading) && (
        <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
          <Spin size="large" />
          <span className="text-xs font-semibold text-gray-600">
            Loading Map Data...
          </span>
        </div>
      )}

      {/* Map Control Toolbar */}
      <div className="absolute top-3 left-3 z-[400] flex items-center gap-2 bg-white/90 backdrop-blur-md p-1.5 rounded-lg shadow-md border border-gray-200/80">
        <AntTooltip title="Switch Map View">
          <Button
            size="small"
            type={mapTileType === "satellite" ? "primary" : "default"}
            icon={<Layers className="w-3.5 h-3.5" />}
            onClick={() =>
              setMapTileType(
                mapTileType === "streets" ? "satellite" : "streets",
              )
            }
            className="text-xs flex items-center gap-1 font-medium"
          >
            {mapTileType === "streets" ? "Satellite" : "Street Map"}
          </Button>
        </AntTooltip>

        <AntTooltip title="Reset Zoom to Fit All Markers">
          <Button
            size="small"
            icon={<RefreshCw className="w-3.5 h-3.5" />}
            onClick={handleRecenter}
            disabled={locations.length === 0}
            className="text-xs flex items-center gap-1"
          >
            Fit Bounds
          </Button>
        </AntTooltip>

        <div className="h-4 w-px bg-gray-300 mx-1" />

        <div className="flex items-center gap-1 text-xs px-2 text-gray-600 font-semibold">
          <MapPin className="w-3.5 h-3.5 text-red-600" />
          <span>{locations.length} Points</span>
        </div>
      </div>

      {/* Empty State Overlay */}
      {isLeafletReady && !loading && locations.length === 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[400] bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200 text-xs font-medium text-gray-600 flex items-center gap-2">
          <Navigation className="w-4 h-4 text-blue-500 animate-bounce" />
          <span>
            No check-in location records found for the selected filters.
          </span>
        </div>
      )}

      {/* Map DOM Container */}
      <div ref={mapContainerRef} className="w-full h-full" />
    </div>
  );
};
